import { useAppStore } from '@/store/useAppStore'
import { useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { WorkloadModal } from '@/components/ui/WorkloadModal'
import { Avatar } from '@/components/ui/Avatar'
import { Search, Bell, Plus, Columns, Calendar, LayoutDashboard, UserPlus, Settings, Palette, X, Film, Layers, Image, Inbox, Users, Grid2X2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { SyncHealthIndicator } from '@/components/ui/SyncHealthIndicator'
import { useState, useRef, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { InviteModal } from '@/components/project/InviteModal'
import { ProjectSettingsModal } from '@/components/project/ProjectSettingsModal'

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const DELIVERABLE_LABELS = { reels: 'Reels', graphics: 'Graphics', photos: 'Photos' }


const NOTIF_ICONS = { assignment: '📋', mention: '@', approval: '✓', rejection: '↩', due_date: '📅' }

function NotificationItem({ n, onRead, onNavigate, onDismiss }) {
  const [dismissed, setDismissed] = useState(false)

  // Auto-dismiss read notifications after 3s
  useEffect(() => {
    if (!n.read) return
    const timer = setTimeout(() => setDismissed(true), 3000)
    return () => clearTimeout(timer)
  }, [n.read])

  // After fade-out animation completes, actually remove
  useEffect(() => {
    if (!dismissed) return
    const timer = setTimeout(() => onDismiss(n.id), 300)
    return () => clearTimeout(timer)
  }, [dismissed])

  function formatTime(iso) {
    if (!iso) return ''
    const d = new Date(iso)
    const now = new Date()
    const diff = (now - d) / 1000
    if (diff < 60) return 'just now'
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 1, height: 'auto' }}
      animate={{ opacity: dismissed ? 0 : 1, height: dismissed ? 0 : 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
      className="overflow-hidden"
    >
      <button
        onClick={() => { onRead(n.id); if (n.projectId) onNavigate(n) }}
        className={cn(
          'group/notif w-full flex items-start gap-3 px-4 py-3 text-left border-b border-border/50 last:border-0 transition-colors hover:bg-surface-raised active:scale-[0.99]',
          !n.read && 'bg-accent/5'
        )}
      >
        <span className="text-base flex-shrink-0 mt-0.5">{NOTIF_ICONS[n.type] || '•'}</span>
        <div className="flex-1 min-w-0">
          <p className={cn('text-xs leading-snug truncate', n.read ? 'text-text-muted' : 'text-text font-medium')}>{n.title}</p>
          {n.body && <p className="text-[11px] text-text-faint mt-0.5 truncate">{n.body}</p>}
          <p className="text-[10px] text-text-faint mt-1 tabular-nums">{formatTime(n.createdAt)}</p>
        </div>
        {!n.read && <span className="w-2 h-2 rounded-full bg-accent flex-shrink-0 mt-1.5" />}
        <span
          role="button"
          onClick={(e) => { e.stopPropagation(); setDismissed(true) }}
          className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-md flex items-center justify-center text-text-faint opacity-0 group-hover/notif:opacity-100 hover:!text-text hover:bg-surface-raised transition-all"
        >
          <X size={11} />
        </span>
      </button>
    </motion.div>
  )
}

function NotificationDropdown({ notifications, onClose, onRead, onReadAll, onNavigate, projectId, projectName }) {
  const ref = useRef(null)
  const [hidden, setHidden] = useState(new Set())
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) onClose() }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const base = filter === 'project' && projectId
    ? notifications.filter((n) => n.projectId === projectId)
    : notifications
  const visible = base.filter((n) => !hidden.has(n.id)).slice(0, 20)

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.96, y: -4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, y: -4 }}
      transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
      style={{ transformOrigin: 'top right' }}
      className="absolute right-0 top-full mt-2 w-80 bg-surface border border-border rounded-2xl shadow-2xl shadow-black/30 overflow-hidden z-50"
    >
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-text">Notifications</span>
          {notifications.some((n) => !n.read) && (
            <button onClick={onReadAll} className="text-[11px] text-accent hover:opacity-80 transition-opacity">
              Mark all read
            </button>
          )}
        </div>
        {projectId && (
          <div className="flex gap-1">
            {[{ id: 'all', label: 'All' }, { id: 'project', label: projectName || 'This project' }].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={cn(
                  'text-[11px] font-medium px-2.5 py-1 rounded-lg transition-colors',
                  filter === tab.id ? 'bg-accent/15 text-accent' : 'text-text-faint hover:text-text'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="max-h-80 overflow-y-auto">
        {visible.length === 0 ? (
          <p className="text-xs text-text-faint text-center py-8">No notifications</p>
        ) : (
          <AnimatePresence mode="popLayout">
            {visible.map((n) => (
              <NotificationItem
                key={n.id}
                n={n}
                onRead={onRead}
                onNavigate={onNavigate}
                onDismiss={(id) => setHidden((prev) => new Set(prev).add(id))}
              />
            ))}
          </AnimatePresence>
        )}
      </div>
    </motion.div>
  )
}

const VIEWS = [
  { id: 'dashboard',  label: 'Dashboard',  icon: LayoutDashboard },
  { id: 'board',      label: 'Board',      icon: Columns },
  { id: 'calendar',   label: 'Calendar',   icon: Calendar },
  // { id: 'brand',      label: 'Brand',      icon: Palette },
  { id: 'instagrid',  label: 'InstaGrid',  icon: Grid2X2 },
]

function ViewTab({ view, active, onClick }) {
  return (
    <button
      onClick={() => onClick(view.id)}
      className={cn(
        'flex items-center gap-2 px-4 h-12 rounded-xl text-sm font-medium transition-all duration-150 active:scale-[0.97] flex-shrink-0',
        active ? 'bg-surface-raised text-text' : 'text-text-muted hover:text-text'
      )}
    >
      <view.icon size={19} />
      {view.label}
    </button>
  )
}

export function TopBar({ onSearchOpen }) {
  const { currentUser, activeProjectId, projects, activeView, setActiveView, addTask, sections, setSelectedTask, notifications, markNotificationRead, markAllNotificationsRead, _membersCache, ensureMembers } = useAppStore()
  const canInvite = useAppStore((s) => s.canDo(s.activeProjectId, 'invite_members'))
  const location = useLocation()
  const navigate = useNavigate()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [addTaskOpen, setAddTaskOpen] = useState(false)
  const [workloadOpen, setWorkloadOpen] = useState(false)
  const addTaskRef = useRef(null)

  const isProjectView = location.pathname.startsWith('/project')
  const project = projects.find((p) => p.id === activeProjectId)
  const unread = notifications.filter((n) => !n.read).length

  // Content sections (not approval/complete) for the quick-add picker — misc is allowed
  const contentSections = sections.filter((s) => s.projectId === activeProjectId && s.type !== 'approval' && s.type !== 'complete')

  const SECTION_ICONS = { reels: Film, graphics: Layers, photos: Image, misc: Inbox }

  function handleQuickAdd(sectionId) {
    setAddTaskOpen(false)
    const section = sections.find((s) => s.id === sectionId)
    if (section) {
      const id = addTask({ title: 'New task', sectionId: section.id, projectId: activeProjectId })
      setSelectedTask(id)
    }
  }

  function handleAddTaskClick() {
    if (contentSections.length <= 1) {
      // Only one content section (or none) — add directly
      const section = contentSections[0] || sections.find((s) => s.projectId === activeProjectId)
      if (section) {
        const id = addTask({ title: 'New task', sectionId: section.id, projectId: activeProjectId })
        setSelectedTask(id)
      }
    } else {
      setAddTaskOpen((v) => !v)
    }
  }

  // Close add-task dropdown on outside click
  useEffect(() => {
    if (!addTaskOpen) return
    function handler(e) { if (addTaskRef.current && !addTaskRef.current.contains(e.target)) setAddTaskOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [addTaskOpen])

  function getMobilePageTitle() {
    if (location.pathname === '/home') return 'Home'
    if (location.pathname === '/my-tasks') return 'My Tasks'
    if (location.pathname === '/inbox') return 'Inbox'
    if (location.pathname === '/reporting') return 'Reporting'
    if (location.pathname === '/profile') return 'Profile'
    return 'PRJCT'
  }

  return (
    <>
      {/* ── Desktop header (hidden on mobile) ── */}
      <div className="hidden md:flex items-center h-20 border-b border-border px-6 gap-3 flex-shrink-0 bg-surface min-w-0" role="banner">
        {/* Project name */}
        {isProjectView && project && (
          <div className="flex items-center gap-3 min-w-0 mr-2">
            <span className="flex-shrink-0 rounded-sm" style={{ background: project.color, width: 10, height: 10 }} aria-hidden="true" />
            <span className="font-semibold text-lg text-text truncate">{project.name}</span>
          </div>
        )}

        {/* View tabs */}
        {isProjectView && (
          <nav data-tour="view-tabs" aria-label="Project views" className="flex items-center gap-0.5 min-w-0 overflow-x-auto scrollbar-none">
            {VIEWS.map((v) => (
              <ViewTab key={v.id} view={v} active={activeView === v.id} onClick={setActiveView} />
            ))}
          </nav>
        )}

        <div className="flex-1" />

        {/* Search — icon-only; click (or ⌘K) opens full search modal */}
        <button
          onClick={onSearchOpen}
          aria-label="Search tasks and projects (⌘K)"
          title="Search (⌘K)"
          className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl border border-border text-text-muted hover:text-text hover:border-accent/40 transition-colors active:scale-[0.97]"
        >
          <Search size={19} aria-hidden="true" />
        </button>

        {/* Project actions */}
        {isProjectView && (
          <>
            <button
              onClick={() => { setWorkloadOpen(true); if (activeProjectId) ensureMembers(activeProjectId) }}
              aria-label="View team workload"
              className="flex-shrink-0 flex items-center gap-2 h-12 px-5 rounded-xl border border-border text-text-muted hover:text-text hover:border-accent/40 text-sm transition-all active:scale-[0.97]"
            >
              <Users size={18} aria-hidden="true" />
              <span className="hidden lg:inline">Team</span>
            </button>
            {canInvite && (
              <button
                onClick={() => setInviteOpen(true)}
                aria-label="Invite to project"
                className="flex-shrink-0 flex items-center gap-2 h-12 px-5 rounded-xl border border-border text-text-muted hover:text-text hover:border-accent/40 text-sm transition-all active:scale-[0.97]"
              >
                <UserPlus size={18} aria-hidden="true" />
                <span className="hidden lg:inline">Invite</span>
              </button>
            )}
            <button
              onClick={() => setSettingsOpen(true)}
              aria-label="Project settings"
              className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl border border-border text-text-muted hover:text-text transition-colors active:scale-[0.97]"
            >
              <Settings size={19} aria-hidden="true" />
            </button>
          </>
        )}

        {/* Quick add task */}
        {isProjectView && (
          <div className="relative flex-shrink-0" ref={addTaskRef}>
            <Button data-tour="add-task" size="xl" variant="primary" onClick={handleAddTaskClick} aria-label="Add new task">
              <Plus size={18} aria-hidden="true" />
              Add task
            </Button>
            <AnimatePresence>
              {addTaskOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96, y: -4 }}
                  transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
                  style={{ transformOrigin: 'top right' }}
                  className="absolute right-0 top-full mt-1.5 w-48 bg-surface border border-border rounded-xl shadow-2xl shadow-black/30 overflow-hidden z-50 py-1"
                >
                  <p className="text-[10px] font-bold uppercase tracking-widest text-text-faint px-3 py-1.5">Add task to</p>
                  {contentSections.map((s) => {
                    const Icon = SECTION_ICONS[s.type] || SECTION_ICONS[s.deliverableType] || Layers
                    return (
                      <button
                        key={s.id}
                        onClick={() => handleQuickAdd(s.id)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-text hover:bg-surface-raised transition-colors active:scale-[0.98]"
                      >
                        <Icon size={14} className="text-text-muted flex-shrink-0" />
                        {s.name}
                      </button>
                    )
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        <div className="flex-shrink-0"><ThemeToggle /></div>

        <div className="flex-shrink-0"><SyncHealthIndicator /></div>

        {/* Notifications */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setNotifOpen((v) => !v)}
            aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
            className="relative w-12 h-12 flex items-center justify-center rounded-xl text-text-muted hover:text-text hover:bg-surface-raised transition-colors active:scale-[0.97]"
          >
            <Bell size={21} aria-hidden="true" />
            {unread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-accent text-[9px] font-bold text-black flex items-center justify-center px-1 leading-none" aria-hidden="true">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </button>
          <AnimatePresence>
            {notifOpen && (
              <NotificationDropdown
                notifications={notifications}
                onClose={() => setNotifOpen(false)}
                onRead={(id) => markNotificationRead(id)}
                onReadAll={() => markAllNotificationsRead()}
                onNavigate={(n) => {
                  setNotifOpen(false)
                  if (n.taskId) setSelectedTask(n.taskId)
                  if (n.projectId) navigate(`/project/${n.projectId}`)
                }}
                projectId={isProjectView ? activeProjectId : null}
                projectName={project?.name}
              />
            )}
          </AnimatePresence>
        </div>

        <button onClick={() => navigate('/profile')} className="flex-shrink-0 active:scale-[0.97] transition-transform" aria-label="Open profile">
          <Avatar user={currentUser} size="xl" className="cursor-pointer" />
        </button>
      </div>

      {/* ── Mobile header (hidden on desktop) ── */}
      <div className="flex md:hidden flex-col border-b border-border bg-surface flex-shrink-0">
        {/* Row 1: title + actions */}
        <div className="flex items-center h-12 px-3 gap-1">
          {isProjectView && project ? (
            <>
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: project.color }} />
              <span className="font-semibold text-sm text-text truncate flex-1 ml-1.5">{project.name}</span>
            </>
          ) : (
            <span className="font-semibold text-sm text-text flex-1">{getMobilePageTitle()}</span>
          )}

          <button
            onClick={onSearchOpen}
            aria-label="Search"
            className="w-10 h-10 flex items-center justify-center rounded-xl text-text-muted active:scale-[0.88] transition-transform"
          >
            <Search size={18} aria-hidden="true" />
          </button>

          <ThemeToggle />

          {isProjectView && (
            <>
              {canInvite && (
                <button
                  onClick={() => setInviteOpen(true)}
                  aria-label="Invite"
                  className="w-10 h-10 flex items-center justify-center rounded-xl text-text-muted active:scale-[0.88] transition-transform"
                >
                  <UserPlus size={18} aria-hidden="true" />
                </button>
              )}
              <button
                onClick={() => setSettingsOpen(true)}
                aria-label="Project settings"
                className="w-10 h-10 flex items-center justify-center rounded-xl text-text-muted active:scale-[0.88] transition-transform"
              >
                <Settings size={18} aria-hidden="true" />
              </button>
              <button
                onClick={handleAddTaskClick}
                aria-label="Add task"
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent text-black active:scale-[0.88] transition-transform"
              >
                <Plus size={18} aria-hidden="true" />
              </button>
            </>
          )}
        </div>

        {/* Row 2: scrollable view tabs (project view only) */}
        {isProjectView && (
          <div className="flex gap-1.5 px-3 pb-2 overflow-x-auto scrollbar-none">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                onClick={() => setActiveView(v.id)}
                className={cn(
                  'flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs whitespace-nowrap flex-shrink-0 transition-all active:scale-[0.95]',
                  activeView === v.id
                    ? 'bg-accent/15 text-accent font-semibold'
                    : 'bg-surface-raised text-text-muted'
                )}
              >
                <v.icon size={13} />
                {v.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {inviteOpen && <InviteModal projectId={activeProjectId} onClose={() => setInviteOpen(false)} />}
        {workloadOpen && <WorkloadModal projectId={activeProjectId} onClose={() => setWorkloadOpen(false)} />}
      </AnimatePresence>
      <ProjectSettingsModal projectId={activeProjectId} open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  )
}
